/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import SvgIcon from '@/pages/home/components/SvgIcon';
import GameSelector from '@/pages/dashboard/components/Navigation/GameSelector';
import SearchPage from '@/pages/dashboard/components/SearchPage';
import '@/pages/dashboard/assets/scss/components/navigation/navigationHeader.scss';
import { useSelector, getLocale, useIntl, useDispatch } from 'umi';
import defaultIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import { getLocalText } from '@/pages/home/utils/utils';
import CommonBubble from '@home/components/CommonBubble';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';

import './HeaderNav.scss';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';

/** hover 停留该时长后上报埋点 */
const PHASE_BUBBLE_IMPRESSION_DELAY_MS = 3000;
/** 鼠标从锚点移到浮窗时，tea 会先 false 再 true；延迟该时长再视为真正收起 */
const PHASE_BUBBLE_HIDE_DEBOUNCE_MS = 500;

export default function HeaderNav(props: any) {
  const { isNormal, onSet, menuWidth } = props;
  const { formatMessage } = useIntl();
  const { currentGame , noAllGamePermission} = useSelector((state) => {
    const { currentGame, gameList, noAllGamePermission } = state.dashboard;
    return {
      currentGame,
      gameList,
      noAllGamePermission,
    };
  });
  // 刷新中英文
  const locale = getLocale();
  const [visible, setVisible] = useState(false);
  // 点击后临时禁用 bubble，鼠标离开后恢复
  const [bubbleDisabled, setBubbleDisabled] = useState(false);
  const dispatch = useDispatch();
  const isEn = locale === LANGUAGE_EN;

  // 此处需要过滤除了 SL、Gl、CBT 的游戏阶段，最多展示 6 个游戏阶段
  const phaseWhiteList = ['SL', 'GL', 'CBT', 'EA', 'Test'];
  const gamePhaseList = currentGame?.game_phase_list
    ?.filter((phase: { phase_type: string; }, index: number) => phaseWhiteList?.includes(phase.phase_type) && index < 6)
    .reverse();

  // ---- Phase Bubble hover 3s 打点逻辑 ----
  const phaseBubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseHideDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasReportedPhaseRef = useRef(false);

  const clearPhaseBubbleTimer = useCallback((): void => {
    if (phaseBubbleTimerRef.current) {
      clearTimeout(phaseBubbleTimerRef.current);
      phaseBubbleTimerRef.current = null;
    }
  }, []);

  const clearPhaseHideDebounce = useCallback((): void => {
    if (phaseHideDebounceRef.current) {
      clearTimeout(phaseHideDebounceRef.current);
      phaseHideDebounceRef.current = null;
    }
  }, []);

  // 切换游戏时重置埋点标记
  useEffect(() => {
    hasReportedPhaseRef.current = false;
    clearPhaseBubbleTimer();
    clearPhaseHideDebounce();
  }, [currentGame?.game_code, clearPhaseBubbleTimer, clearPhaseHideDebounce]);

  useEffect(() => {
    return () => {
      clearPhaseBubbleTimer();
      clearPhaseHideDebounce();
    };
  }, [clearPhaseBubbleTimer, clearPhaseHideDebounce]);

  const handlePhaseBubbleVisibleChange = useCallback(
    (bubbleVisible: boolean): void => {
      if (bubbleVisible) {
        clearPhaseHideDebounce();
        if (hasReportedPhaseRef.current || phaseBubbleTimerRef.current) {
          return;
        }
        phaseBubbleTimerRef.current = setTimeout(() => {
          phaseBubbleTimerRef.current = null;
          if (hasReportedPhaseRef.current) {
            return;
          }
          hasReportedPhaseRef.current = true;
          buriedBtnPoint({
            pageId: '100001',
            buttonId: '100001004',
            extInfo: `game_phase_bubble:${currentGame?.game_code || ''}`,
            buttonName: 'GamePhaseBubble',
            uidType: 'game_code',
            uid: currentGame?.game_code || '',
            gameName: currentGame?.game_name || currentGame?.game_cn_name || '',
            typeId: 'dashboard_pc_v2',
          });
        }, PHASE_BUBBLE_IMPRESSION_DELAY_MS);
      } else {
        phaseHideDebounceRef.current = setTimeout(() => {
          phaseHideDebounceRef.current = null;
          clearPhaseBubbleTimer();
          hasReportedPhaseRef.current = false;
        }, PHASE_BUBBLE_HIDE_DEBOUNCE_MS);
      }
    },
    [currentGame, clearPhaseBubbleTimer, clearPhaseHideDebounce],
  );

  const formatPhaseDateRange = (startDate?: string, endDate?: string): string => {
    const formattedStart = startDate?.replaceAll('-', '.') || '-';
    if (!endDate) {
      return formattedStart;
    }
    if (moment(endDate).isAfter(moment(), 'day')) {
      return isEn ? `Since ${formattedStart}` : `${formattedStart}~至今`;
    }
    return `${formattedStart}~${endDate.replaceAll('-', '.')}`;
  };

  // Bubble 内容：优先展示 game_phase_list，没有则 fallback 到 release_date，都没有则不展示
  const getPhaseBubbleContent = () => {
    if (gamePhaseList?.length) {
      return (
        <div className="dbd-nav-phase-bubble">
          <div className="dbd-nav-phase-bubble__row">
            <span className="dbd-nav-phase-bubble__hd">{getLanguage(isEn, 'Stage', '发行阶段')}</span>
            <span className="dbd-nav-phase-bubble__hd">{getLanguage(isEn, 'Name', '阶段名称')}</span>
            <span className="dbd-nav-phase-bubble__hd">{getLanguage(isEn, 'Date', '日期')}</span>
          </div>
          {gamePhaseList.map((phase: any, index: number) => (
            <React.Fragment key={index}>
              <div className="dbd-nav-phase-bubble__row dbd-nav-phase-bubble__row--data">
                <span className="dbd-nav-phase-bubble__td">{phase?.phase_name || '-'}</span>
                <span className="dbd-nav-phase-bubble__td">{phase?.phase_type || '-'}</span>
                <span className="dbd-nav-phase-bubble__td">{formatPhaseDateRange(phase?.start_date, phase?.end_date)}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      );
    }
    if (currentGame?.release_date) {
      return (
        <div className="dbd-nav-phase-bubble dbd-nav-phase-bubble--simple">
          <span className="dbd-nav-phase-bubble__hd">{getLanguage(isEn, 'Release Date', '发布日期')}</span>
          <span className="dbd-nav-phase-bubble__td">{currentGame.release_date?.replaceAll('-', '.')}</span>
        </div>
      );
    }
    return null;
  };
  const phaseBubbleContent = bubbleDisabled ? null : getPhaseBubbleContent();

  useEffect(() => {
    // 请求最近浏览的游戏
    dispatch({
      type: 'dashboard/save',
      payload: {
        showContentMask: visible,
      },
    });

    dispatch({
      type: 'home/save',
      payload: {
        showGlobalMask: visible ? 'transparent' : false,
      },
    });
  }, [visible]);

  return (
    <div className="navmenu-hd">
      {isNormal ? (
        <div>
          <div className="navmenu-hd-title">
            {/* dashboard */}
            <div className="width-text">
              {formatMessage({ id: 'dashboard' })}
            </div>
          </div>
          <CommonBubble
            dark
            placement="bottom"
            trigger="hover"
            content={phaseBubbleContent}
            overlayClassName="dbd-nav-phase-bubble-overlay"
            onVisibleChange={handlePhaseBubbleVisibleChange}
          >
            <div
              className="navmenu-hd-info"
              onClick={() => {
                setBubbleDisabled(true);
                setVisible(!visible);
              }}
              onMouseLeave={() => {
                if (bubbleDisabled) {
                  setBubbleDisabled(false);
                }
              }}
            >
              {/* navmenu-hd-info-imgwidth 长图片的样式使用这个类名  navmenu-hd-info-img 短图片样式*/}
              <img
                src={!noAllGamePermission ? currentGame?.cover || defaultIcon : defaultIcon}
                onError={(e: any) => {
                  e.target.src = defaultIcon;
                }}
                className="navmenu-hd-info-img"
              />
              {!noAllGamePermission && <div className="navmenu-hd-info-title">
                <div className="wrapper-context">
                  <div className="wrapper-context-bd">
                    {/* <Bubble
                    placement="right"
                    className="bubble-box"
                    trigger="hover"
                    dark
                    content={
                      <>
                        <div className="bubble-text">
                          {getLocalText(
                            locale,
                            currentGame,
                            'game_name',
                            'game_cn_name',
                          )}
                        </div>
                        {gamePhaseList?.map((gamePhase: any, index: number) => {
                          const badgeClassName = formatGamePhase(gamePhase?.phase_type, 'className', false, gamePhase?.phase_type);
                          return (
                            <div className={`bubble-flex`} key={index}>
                              <div className={`bubble-tab ${badgeClassName}`}>
                                {formatGamePhase(gamePhase?.game_phase, 'type', false, gamePhase?.phase_type)}
                              </div>
                              <div
                                className={`bubble-time ${index === gamePhaseList?.length - 1
                                  ? 'bubble-time-now'
                                  : ''
                                }`}
                              >
                                {gamePhase?.start_date} ~ {gamePhase?.end_date}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    }
                  >
                    {lastestPhase?.phase_type && <span
                      className={`text-btn ${formatGamePhase(lastestPhase?.phase_type, 'className', false, lastestPhase?.phase_type)}`}
                    >
                      {formatGamePhase(lastestPhase?.phase_type || "", 'type', false, lastestPhase?.phase_type)}
                    </span>}
                  </Bubble> */}
                    {getLocalText(
                      locale,
                      currentGame,
                      'game_name',
                      'game_cn_name',
                    )}
                  </div>
                </div>
              </div>}
              {/* <div className='navmenu-hd-info-badge'>9</div>*/}
              <div className="navmenu-hd-info-icon dbd-navmenu-hd-info-icon">
                <SvgIcon type="left-turn-right" />
              </div>
            </div>
          </CommonBubble>
          {/* 搜索框*/}
          <SearchPage />
          {/* 游戏选择弹出框*/}
          {/* 用visible && 的方式是为了解决搜素后关闭弹窗，打开后仍然是搜素界面的问题 */}
          {visible && (
            <GameSelector visible={visible} setVisible={setVisible} menuWidth={menuWidth} />
          )}
        </div>
      ) : (
        <>
          <div
            className="header-width"
            onClick={() => {
              !isNormal ? onSet(false) : '';
            }}
          >
            {' '}
            <SvgIcon className="header-width-title" type="merit" />
          </div>
          <div
            className="header-width"
            onClick={() => {
              !isNormal ? onSet(false) : '';
            }}
          >
            {' '}
            <SvgIcon className="header-width-icon" type="left-turn-right" />
          </div>
          <div
            className="header-width"
            onClick={() => {
              !isNormal ? onSet(false) : '';
            }}
          >
            {' '}
            <SvgIcon className="header-width-icon" type="search" />
          </div>
        </>
      )}
    </div>
  );
}
